import { useTranslation } from "react-i18next";
import type { AnalyticsActivityBreakdownDto } from "@normax-performance/shared-types";
import { useLocale } from "../../../i18n/LocaleProvider";
import { formatAnalyticsMinutes } from "../../../lib/format";

export interface ActivitiesViewProps {
  data: AnalyticsActivityBreakdownDto;
}

/**
 * Neutral + Unnormed (docs/.../FORENSIC_PRODUCT_BRIEF.md §10, this slice's
 * spec §17) — two clearly separate sections, NEITHER carrying a performance
 * percentage or zone (the DTOs themselves have no such field — nothing to
 * accidentally render). A visible explanatory line makes the exclusion
 * honest rather than implicit.
 *
 * Both tables carry `nx-scroll-table` (shared-ui/patterns/sticky-headers.css)
 * for the same sticky column-header treatment Radnici/Proizvodi/Operacije
 * already had — this tab was the one confirmed gap (docs/
 * NORMAX_PERFORMANCE_REPORTS_ANALYTICS_SCROLL_STICKY_FORENSIC_REPORT.md §7):
 * previously the only Analytics table with no sticky header at all.
 */
export function ActivitiesView({ data }: ActivitiesViewProps) {
  const { t } = useTranslation("analytics");
  const { intlLocale } = useLocale();

  return (
    <div>
      <p className="nx-analytics__activities-note">{t("activities.explanation")}</p>
      <div className="nx-analytics__activities-grid">
        <div>
          <p className="nx-analytics__section-title">{t("activities.neutralTitle")}</p>
          {data.neutralActivities.length === 0 ? (
            <p>{t("activities.emptyNeutral")}</p>
          ) : (
            <div className="nx-analytics-table-wrap nx-scroll-table">
              <table className="nx-analytics-table nx-table">
                <thead>
                  <tr>
                    <th>{t("activities.activity")}</th>
                    <th>{t("activities.category")}</th>
                    <th className="nx-analytics-table__num">{t("activities.totalMinutes")}</th>
                    <th className="nx-analytics-table__num">{t("activities.workerCount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.neutralActivities.map((n) => (
                    <tr key={n.neutralActivityId}>
                      <td>{n.name}</td>
                      <td>{n.category ?? "—"}</td>
                      <td className="nx-analytics-table__num">{formatAnalyticsMinutes(n.totalMinutes, intlLocale)}</td>
                      <td className="nx-analytics-table__num">{n.workerCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div>
          <p className="nx-analytics__section-title">{t("activities.unnormedTitle")}</p>
          {data.unnormedWork.length === 0 ? (
            <p>{t("activities.emptyUnnormed")}</p>
          ) : (
            <div className="nx-analytics-table-wrap nx-scroll-table">
              <table className="nx-analytics-table nx-table">
                <thead>
                  <tr>
                    <th>{t("activities.operation")}</th>
                    <th>{t("activities.product")}</th>
                    <th className="nx-analytics-table__num">{t("activities.totalMinutes")}</th>
                    <th className="nx-analytics-table__num">{t("activities.workerCount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.unnormedWork.map((u) => (
                    <tr key={`${u.operationId}::${u.productId ?? "none"}`}>
                      <td>
                        {u.operationName}
                        {u.operationCode ? <span className="nx-analytics-table__sub">{u.operationCode}</span> : null}
                      </td>
                      <td>{u.productName ?? t("activities.noProduct")}</td>
                      <td className="nx-analytics-table__num">{formatAnalyticsMinutes(u.totalMinutes, intlLocale)}</td>
                      <td className="nx-analytics-table__num">{u.workerCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
